import React from "react";
import type { DialogProps } from "@radix-ui/react-dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "../utils";

const DialogContent = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Content>>;
}) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid h-[456px] w-full max-w-[648px] translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const Command = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive>>;
}) => (
  <CommandPrimitive
    ref={ref}
    filter={() => 1}
    className={cn(
      "flex h-fit max-h-[456px] w-full flex-col overflow-hidden rounded-lg border border-border-tertiary bg-background-primary shadow-lg",
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends DialogProps {
  /** Visually hidden accessible name for the dialog. */
  title?: string;
  onCloseAutoFocus?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>["onCloseAutoFocus"];
  contentClassName?: string;
  commandClassName?: string;
}

const CommandDialog = ({
  children,
  title,
  onCloseAutoFocus,
  contentClassName,
  commandClassName,
  ...props
}: CommandDialogProps) => {
  return (
    <DialogPrimitive.Root {...props}>
      <DialogContent
        className={cn("rounded-lg border-none p-0 shadow-none", contentClassName)}
        onCloseAutoFocus={onCloseAutoFocus}
        {...(title === undefined ? {} : { "aria-describedby": undefined })}
      >
        {title !== undefined && <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>}
        <Command shouldFilter={false} className={commandClassName}>
          {children}
        </Command>
      </DialogContent>
    </DialogPrimitive.Root>
  );
};

const CommandInput = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Input>>;
}) => (
  <div className="flex items-center border-border-tertiary border-b px-3" cmdk-input-wrapper="">
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.List>>;
}) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(
      "scrollbar scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-scrollbar-bgHover overflow-y-auto overflow-x-hidden",
      className,
    )}
    {...props}
  />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Empty>>;
}) => <CommandPrimitive.Empty ref={ref} className="py-3 text-center text-sm" {...props} />;

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Group>>;
}) => (
  <CommandPrimitive.Group ref={ref} className={cn("overflow-hidden p-1 text-text-primary", className)} {...props} />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Separator>>;
}) => <CommandPrimitive.Separator ref={ref} className={cn("-mx-1 h-px bg-border", className)} {...props} />;
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = ({
  ref,
  className,
  value,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & { value: string } & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Item>>;
}) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-1 rounded-md px-3 text-xs outline-hidden data-[disabled=true]:pointer-events-none data-[selected='true']:bg-background-primaryHover",
      className,
    )}
    {...props}
    value={CSS.escape(value)}
  >
    {children}
  </CommandPrimitive.Item>
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn("ml-auto text-text-secondary text-xs tracking-widest", className)} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command as RemiCommand,
  CommandDialog as RemiCommandDialog,
  CommandEmpty as RemiCommandEmpty,
  CommandGroup as RemiCommandGroup,
  CommandInput as RemiCommandInput,
  CommandItem as RemiCommandItem,
  CommandList as RemiCommandList,
};
